import { BaseObjDao } from '@/dao';
import { ShootingResult } from '@/objects/generated';
import { LoaderWithReset } from '@/utilities/LoaderWithReset';
import type { ContestantId, RaceContestantId, RaceId } from '@/utilities/typealiases';
import { defineStore } from 'pinia';
import { useEventStore } from './eventStore';
import { addToMapWithArray, addToMapWithArrayAndEnsureTheValueIsUniqueInArrayByComparer } from '@/utilities/arrayUtils';
import { watch } from 'vue';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';

type ShootingResultId = string;

type LoaderValueType = {
    shootingResults: Map<ShootingResultId, ShootingResult>;
    raceContestantIdShootingResultMap: Map<RaceContestantId, ShootingResult[]>;
    contestantIdShootingResultMap: Map<ContestantId, ShootingResult[]>;
    raceShootingResultMap: Map<RaceId, ShootingResult[]>;
};

interface ShootingResultSubscriptionOptions {
    triggerForExistingResults?: boolean;
}

export const useShootingResultStore = defineStore('shootingresultsstore', () => {
    const eventStore = useEventStore();

    /**
     * List of all the active subscriptions for the shooting results
     */
    const shootingResultSubscriptions = new Set<(sr: ShootingResult[]) => void>();

    const loader = new LoaderWithReset<LoaderValueType>({
        defaultValue: {
            shootingResults: new Map(),
            raceContestantIdShootingResultMap: new Map(),
            contestantIdShootingResultMap: new Map(),
            raceShootingResultMap: new Map(),
        },
        loaderFunction: async () => {
            const dbShootingRes = await BaseObjDao.Load<ShootingResult>(ShootingResult, eventStore.eventID);

            const shootingResults = new Map<ShootingResultId, ShootingResult>();
            const raceContestantShootingResultMap = new Map<RaceContestantId, ShootingResult[]>();
            const contestantIdShootingResultMap = new Map<ContestantId, ShootingResult[]>();
            const raceShootingResultMap = new Map<RaceId, ShootingResult[]>();
            dbShootingRes.forEach((sr) => {
                addToMapWithArray(raceContestantShootingResultMap, sr.RaceContestantId, sr);
                addToMapWithArray(contestantIdShootingResultMap, sr.ContestantId, sr);
                addToMapWithArray(raceShootingResultMap, sr.RaceId, sr);
                shootingResults.set(sr.Id!, sr);
            });

            return {
                shootingResults: shootingResults,
                raceContestantIdShootingResultMap: raceContestantShootingResultMap,
                contestantIdShootingResultMap: contestantIdShootingResultMap,
                raceShootingResultMap: raceShootingResultMap,
            };
        },
        waitOnBeforeLoad: eventStore.initpromise,
    });

    const reset = () => loader.Reset();

    const ensureLoaded = async () => await loader.Load();

    let signalrUnsubscribeFunction: Function | null = null;
    /**
     * This will trigger every time the eventID changes so we set up the loaders again
     */
    watch(
        () => eventStore.eventID,
        async (nv, ov) => {
            if (nv == ov || nv == 0) {
                return;
            }

            reset();

            if (signalrUnsubscribeFunction != null) {
                await signalrUnsubscribeFunction();
            }

            signalrUnsubscribeFunction = await signalrSubscribe.ShootingResult(
                {
                    EventID: eventStore.eventID,
                },
                async (sr) => await handleShootingResult(sr),
            );
        },
        { immediate: true },
    );

    const handleShootingResult = (sr: ShootingResult[]) => {
        sr.forEach((s) => {
            addToMapWithArrayAndEnsureTheValueIsUniqueInArrayByComparer(
                loader.Value.raceContestantIdShootingResultMap,
                s.RaceContestantId,
                s,
                (add, existing) => add.Id === existing.Id,
            );
            addToMapWithArrayAndEnsureTheValueIsUniqueInArrayByComparer(
                loader.Value.contestantIdShootingResultMap,
                s.ContestantId,
                s,
                (add, existing) => add.Id === existing.Id,
            );
            addToMapWithArrayAndEnsureTheValueIsUniqueInArrayByComparer(
                loader.Value.raceShootingResultMap,
                s.RaceId,
                s,
                (add, existing) => add.Id === existing.Id,
            );
            loader.Value.shootingResults.set(s.Id!, s);
        });

        shootingResultSubscriptions.forEach((cb) => cb(sr));
    };

    const subscribeToShootingResults = (callback: (sr: ShootingResult[]) => void, options?: ShootingResultSubscriptionOptions) => {
        shootingResultSubscriptions.add(callback); // Note, we dont save the options since we never need to access the options again

        // If we should trigger for existing values, do so now
        if (options?.triggerForExistingResults) {
            ensureLoaded().then(() => {
                callback(Array.from(loader.Value.shootingResults.values()));
            });
        }

        // If the calling component is unmounted we need to remove the subscription
        return () => {
            shootingResultSubscriptions.delete(callback);
        };
    };

    return {
        /**
         * Ensure that all the data will be loaded from the server
         */
        EnsureLoaded: ensureLoaded,

        /**
         * Subscribe to shooting results. The callback will be invoked every time we get a new shooting result
         */
        SubscribeToShootingResults: subscribeToShootingResults,

        /**
         * Gets a shooting result by its shootingResultId
         * @param shootingResultId The shooting result to get
         * @returns A shootingresult if one is found, undefined otherwise
         */
        GetShootingResultsByShootingResultId: (shootingResultId: ShootingResultId) => loader.Value.shootingResults.get(shootingResultId),

        /**
         * Gets all shooting results for a specific race
         *
         * Note: This returns the currently loaded data, it does NOT trigger a load. So if you have not ensureLoaded before calling this, you might get an empty array
         * @param raceId The raceId we want shooting results for
         * @returns The shootingresults for this raceId
         */
        GetShootingResultsForRaceId: (raceId: RaceId) => loader.Value.raceShootingResultMap.get(raceId) || [],

        /**
         * Get all shooting results for a specific raceContestantId
         *
         * Note: This returns the currently loaded data, it does NOT trigger a load. So if you have not ensureLoaded before calling this, you might get an empty array
         * @param raceContestantId The raceContestantId we want shootingResults for
         * @returns All the shootingResults for one raceContestantId
         */
        GetShootingResultsForRaceContestantId: (raceContestantId: RaceContestantId) =>
            loader.Value.raceContestantIdShootingResultMap.get(raceContestantId) || [],

        /**
         * Get all shooting results for a specific contestantId
         *
         * Note: This returns the currently loaded data, it does NOT trigger a load. So if you have not ensureLoaded before calling this, you might get an empty array
         * @param contestantId The contestantId we want shooting results for
         * @returns All the shooting results for one contestantId
         */
        GetShootingResultsForContestantId: (contestantId: ContestantId) =>
            loader.Value.contestantIdShootingResultMap.get(contestantId) || [],
    };
});
